import { useState } from "react";
import { Button } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import PeopleAltOutlinedIcon from "@mui/icons-material/PeopleAltOutlined";
import { useScriptMode } from "../../contexts/ScriptModeContext";
import { useAuth } from "../../contexts/AuthContext";
import { useToast } from "../../components/ui/Toast";
import { useGetMyGroupsQuery } from "../../api/groupsApi";
import { AccountHeader, RowGroup, SoftRow } from "../profile/components/AccountSoft";
import JoinGroupModal from "./components/JoinGroupModal";
import "../profile/darkSurface.css";
import ConnectionState from "../../components/ui/ConnectionState";

function isManager(group, currentUserId) {
  if (!Array.isArray(group?.managerIds)) return false;
  return group.managerIds.some((m) => {
    const id = typeof m === "object" ? m?.user?._id || m?.user : m;
    return id && id.toString() === String(currentUserId);
  });
}

/**
 * Gruplarım (mobildeki gibi): üyesi olunan gruplar çizgili satırlarla; satıra
 * basınca grubun İstatistik, Olaylar ve Ünvanlar sekmeleri açılır. Davet kodu
 * ya da bağlantısıyla gruba katılma da buradan.
 */
export default function MemberGroupListPage() {
  const { t } = useScriptMode();
  const toast = useToast();
  const { user } = useAuth();
  const currentUserId = user?._id;
  const { data, isLoading, isFetching, isError, error, refetch } = useGetMyGroupsQuery();
  const groups = data?.data?.groups || [];
  const [joinOpen, setJoinOpen] = useState(false);

  const metaOf = (g) => {
    const role = isManager(g, currentUserId) ? t("groups_role_manager") : t("groups_role_member");
    return `${g.memberCount ?? 0} ${t("groups_member_count")} · ${role}`;
  };

  return (
    <div className="tk-dark w-full max-w-3xl mx-auto flex flex-col gap-4">
      <div className="flex items-center justify-between gap-3">
        <AccountHeader title={t("groups_title")} />
        <Button
          variant="outlined"
          startIcon={<AddIcon />}
          onClick={() => setJoinOpen(true)}
          sx={{ borderRadius: 999, textTransform: "none" }}
        >
          {t("groups_join_cta")}
        </Button>
      </div>

      {isLoading ? (
        <RowGroup>
          {[0, 1, 2].map((i) => (
            <div key={i} className="flex items-center gap-3 px-4 py-3 border-b border-terakkiBorder last:border-b-0">
              <div className="w-10 h-10 rounded-full bg-terakkiSurfaceAlt animate-pulse" />
              <div className="flex-1 flex flex-col gap-1.5">
                <div className="h-3.5 w-1/2 rounded bg-terakkiSurfaceAlt animate-pulse" />
                <div className="h-3 w-1/3 rounded bg-terakkiSurfaceAlt animate-pulse" />
              </div>
            </div>
          ))}
        </RowGroup>
      ) : isError ? (
        <ConnectionState
          error={error}
          onRetry={refetch}
          retrying={isFetching}
          message={t("groups_load_error")}
        />
      ) : groups.length === 0 ? (
        <div className="flex flex-col items-center text-center gap-2 py-12">
          <PeopleAltOutlinedIcon sx={{ fontSize: 40 }} className="text-terakkiBorder" aria-hidden />
          <div className="text-base text-terakkiInk">{t("groups_empty_title")}</div>
          <div className="text-sm text-terakkiNeutral max-w-sm">{t("groups_empty_body")}</div>
        </div>
      ) : (
        <RowGroup>
          {groups.map((g) => (
            <SoftRow
              key={g.id}
              Icon={PeopleAltOutlinedIcon}
              title={g.groupName}
              meta={metaOf(g)}
              to={`/uygulama/gruplar/${g.id}`}
            />
          ))}
        </RowGroup>
      )}

      <JoinGroupModal
        open={joinOpen}
        onClose={() => setJoinOpen(false)}
        onJoined={(groupName) => {
          toast.success(t("group_join_success", { groupName }));
          refetch();
        }}
      />
    </div>
  );
}
